import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Popup } from '../../utils/supabase';
import { useActivePopups } from '../../hooks/usePopups';

interface PopupDisplayProps {
  currentPath?: string;
}

interface DisplayedPopupProps {
  popup: Popup;
  onClose: () => void;
  onButtonClick: () => void;
}

const DisplayedPopup: React.FC<DisplayedPopupProps> = ({ popup, onClose, onButtonClick }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show popup after delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, (popup.delay_seconds || 0) * 1000);

    return () => clearTimeout(timer);
  }, [popup.delay_seconds]);

  const handleClose = useCallback(() => {
    // Call parent's onClose immediately - let parent handle the unmounting
    onClose();
  }, [onClose]);

  useEffect(() => {
    // Auto close popup if specified
    if (popup.auto_close_seconds && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, popup.auto_close_seconds * 1000);

      return () => clearTimeout(timer);
    }
  }, [popup.auto_close_seconds, isVisible, handleClose]);

  useEffect(() => {
    // Handle escape key
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll for modal popups
      if (popup.popup_type === 'modal') {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (popup.popup_type === 'modal') {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isVisible, popup.popup_type, handleClose]);

  const handleButtonClick = useCallback(() => {
    onButtonClick();
    
    if (popup.button_action === 'redirect' && popup.button_url) {
      window.open(popup.button_url, '_blank');
    } else if (popup.button_action === 'close') {
      handleClose();
    }
    // For custom actions, the parent component should handle the logic
  }, [popup.button_action, popup.button_url, onButtonClick, handleClose]);

  const getPositionClasses = () => {
    const base = 'fixed z-50';
    
    switch (popup.popup_type) {
      case 'modal':
        return `${base} inset-0 flex items-center justify-center p-4`;
      
      case 'banner':
        switch (popup.position) {
          case 'top':
            return `${base} top-0 left-0 right-0`;
          case 'bottom':
            return `${base} bottom-0 left-0 right-0`;
          default:
            return `${base} top-0 left-0 right-0`;
        }
      
      case 'slide-in':
        switch (popup.position) {
          case 'left':
            return `${base} left-0 top-1/2 transform -translate-y-1/2`;
          case 'right':
            return `${base} right-0 top-1/2 transform -translate-y-1/2`;
          case 'top':
            return `${base} top-0 left-1/2 transform -translate-x-1/2`;
          case 'bottom':
            return `${base} bottom-0 left-1/2 transform -translate-x-1/2`;
          default:
            return `${base} right-0 top-1/2 transform -translate-y-1/2`;
        }
      
      case 'corner':
        switch (popup.position) {
          case 'top-left':
            return `${base} top-4 left-4`;
          case 'top-right':
            return `${base} top-4 right-4`;
          case 'bottom-left':
            return `${base} bottom-4 left-4`;
          case 'bottom-right':
          default:
            return `${base} bottom-4 right-4`;
        }
      
      default:
        return `${base} inset-0 flex items-center justify-center p-4`;
    }
  };

  const getSizeClasses = () => {
    if (popup.popup_type === 'banner') {
      return 'w-full';
    }
    
    // If it's an image-only popup, let it size to the image with larger max dimensions
    if (popup.image_url && !popup.button_text) {
      return 'w-auto max-w-[95vw] max-h-[95vh]';
    }
    
    // For image popups with buttons, make them larger
    if (popup.image_url) {
      return 'w-auto max-w-[90vw] max-h-[90vh]';
    }
    
    switch (popup.width) {
      case 'sm':
        return 'w-80 max-w-sm';
      case 'md':
        return 'w-96 max-w-md';
      case 'lg':
        return 'w-[32rem] max-w-2xl';
      case 'xl':
        return 'w-[48rem] max-w-4xl';
      case 'full':
        return 'w-full max-w-full';
      default:
        return 'w-96 max-w-md';
    }
  };

  const getAnimationClasses = () => {
    switch (popup.popup_type) {
      case 'modal':
        return isVisible 
          ? 'animate-in fade-in-0 zoom-in-95 duration-300' 
          : 'animate-out fade-out-0 zoom-out-95 duration-300';
      
      case 'banner':
        const isTop = popup.position === 'top';
        return isVisible
          ? `animate-in ${isTop ? 'slide-in-from-top' : 'slide-in-from-bottom'} duration-300`
          : `animate-out ${isTop ? 'slide-out-to-top' : 'slide-out-to-bottom'} duration-300`;
      
      case 'slide-in':
        const slideDirection = popup.position === 'left' ? 'slide-in-from-left' : 'slide-in-from-right';
        const slideOutDirection = popup.position === 'left' ? 'slide-out-to-left' : 'slide-out-to-right';
        return isVisible
          ? `animate-in ${slideDirection} duration-300`
          : `animate-out ${slideOutDirection} duration-300`;
      
      case 'corner':
        return isVisible
          ? 'animate-in slide-in-from-bottom-2 fade-in-0 duration-300'
          : 'animate-out slide-out-to-bottom-2 fade-out-0 duration-300';
      
      default:
        return isVisible 
          ? 'animate-in fade-in-0 zoom-in-95 duration-300' 
          : 'animate-out fade-out-0 zoom-out-95 duration-300';
    }
  };

  // Component always renders when mounted - parent controls mounting/unmounting

  const popupStyle = {
    backgroundColor: popup.background_color || '#FFFFFF',
    color: popup.text_color || '#1F2937',
    borderColor: popup.border_color || '#E5E7EB',
  };

  const buttonStyle = {
    backgroundColor: popup.button_color || '#DC2626',
    color: popup.button_text_color || '#FFFFFF',
  };

  return (
    <div className={getPositionClasses()}>
      {/* Backdrop for modal */}
      {popup.popup_type === 'modal' && (
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isVisible ? 'bg-opacity-50' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        />
      )}
      
      {/* Popup Content */}
      <div
        className={`
          relative bg-white shadow-xl border
          ${getSizeClasses()}
          ${getAnimationClasses()}
          ${popup.popup_type === 'banner' ? 'rounded-none' : ''}
          ${popup.popup_type === 'corner' ? 'max-w-sm' : ''}
          ${popup.image_url && !popup.button_text ? 'rounded-none overflow-hidden' : 'rounded-lg'}
        `}
        style={popupStyle}
      >
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 transition-colors z-50 bg-white rounded-full shadow-md hover:shadow-lg cursor-pointer"
          aria-label="Close popup"
          type="button"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className={`${popup.popup_type === 'banner' ? 'py-4' : ''}`}>
          {/* Image-only popup - no padding, just the image */}
          {popup.image_url && !popup.button_text ? (
            <div className="relative">
              <img
                src={popup.image_url}
                alt=""
                className="w-full h-auto block max-w-[95vw] max-h-[95vh] object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="p-6">
              {/* Image */}
              {popup.image_url && (
                <div className="mb-4">
                  <img
                    src={popup.image_url}
                    alt=""
                    className="w-full h-auto object-contain rounded-lg max-h-[70vh]"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Button */}
              {popup.button_text && (
                <div className="flex justify-end">
                  <button
                    onClick={handleButtonClick}
                    className="px-4 py-2 rounded-md text-sm font-medium transition-colors hover:opacity-90 flex items-center space-x-2"
                    style={buttonStyle}
                  >
                    <span>{popup.button_text}</span>
                    {popup.button_action === 'redirect' && <ExternalLink size={16} />}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cultural decoration for modal popups */}
        {popup.popup_type === 'modal' && (
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-crimson via-mustard to-indigo-deep opacity-60 rounded-t-lg" />
        )}
      </div>
    </div>
  );
};

export const PopupDisplay: React.FC<PopupDisplayProps> = ({ currentPath }) => {
  const { activePopups, recordInteraction, dismissPopup, clickPopup } = useActivePopups(currentPath);
  const [closedPopupIds, setClosedPopupIds] = useState<string[]>([]);
  const [viewedPopupIds, setViewedPopupIds] = useState<string[]>([]);

  // Filter out closed popups
  const availablePopups = activePopups.filter(popup =>
    popup.id && !closedPopupIds.includes(popup.id)
  );

  // Get the first available popup that hasn't been viewed
  const currentPopup = availablePopups.find(popup =>
    popup.id && !viewedPopupIds.includes(popup.id)
  ) || availablePopups[0]; // Fallback to first available if all have been viewed

  useEffect(() => {
    if (currentPopup && currentPopup.id && !viewedPopupIds.includes(currentPopup.id)) {
      setViewedPopupIds(prev => [...prev, currentPopup.id!]);
      recordInteraction(currentPopup.id!, 'viewed');
    }
  }, [currentPopup, viewedPopupIds, recordInteraction]);

  const handleClosePopup = useCallback(() => {
    if (currentPopup && currentPopup.id) {
      // Add to closed list to prevent re-display
      setClosedPopupIds(prev => [...prev, currentPopup.id!]);
      // Record dismissal
      dismissPopup(currentPopup.id!);
    }
  }, [currentPopup, dismissPopup]);

  const handleButtonClick = useCallback(() => {
    if (currentPopup && currentPopup.id) {
      clickPopup(currentPopup.id!);
    }
  }, [currentPopup, clickPopup]);

  // If no current popup, render nothing
  if (!currentPopup) {
    return null;
  }

  return (
    <DisplayedPopup
      key={currentPopup.id} // Add key to force re-render on popup change
      popup={currentPopup}
      onClose={handleClosePopup}
      onButtonClick={handleButtonClick}
    />
  );
};

export default PopupDisplay;